import type { BoardData, Supplier, SupplierKind } from '../model/schema.js';
import { normalizeToken } from '../catalog/catalog.js';

type Locale = 'en' | 'de';

/** Resolved supplier of a material row (U67). */
export interface SupplierInfo {
  /** Stable key: `own`, `house`, `musician:<item id>`, `musician:n:<name>`, `other:<name>`. */
  key: string;
  kind: SupplierKind;
  /** Display name ("Tobias (own)", "House", "Anna"). */
  label: string;
}

/** Unset suppliers are the own material (U67). */
export const OWN: Supplier = { kind: 'own' };

const OWN_LABEL: Record<Locale, string> = { en: 'Own', de: 'Eigenes' };
const OWN_SUFFIX: Record<Locale, string> = { en: 'own', de: 'eigenes' };
const HOUSE_LABEL: Record<Locale, string> = { en: 'House', de: 'Haus' };
const UNNAMED: Record<Locale, string> = { en: 'Unnamed', de: 'Ohne Namen' };

/** Musician object a supplier refers to, if it still exists on the board. */
function musicianOf(data: BoardData, s: Supplier) {
  const item = s.musicianId === undefined ? undefined : data.items[s.musicianId];
  return item?.kind === 'musician' ? item : undefined;
}

/** Name of a musician / other supplier: the musician's current label, else the stored name. */
function nameOf(data: BoardData, s: Supplier): string {
  return (musicianOf(data, s)?.label.trim() || s.name?.trim()) ?? '';
}

/** Stable grouping key of a supplier (musicians by object, else by name). */
export function supplierKey(data: BoardData, s: Supplier = OWN): string {
  if (s.kind === 'own' || s.kind === 'house') return s.kind;
  const musician = musicianOf(data, s);
  if (s.kind === 'musician' && musician !== undefined) return `musician:${musician.id}`;
  const name = normalizeToken(nameOf(data, s));
  return s.kind === 'musician' ? `musician:n:${name}` : `other:${name}`;
}

/**
 * Supplier with its key and display label. The own supplier shows `ownName` ("Tobias (own)");
 * without a name just "Own".
 */
export function supplierInfo(
  data: BoardData,
  s: Supplier = OWN,
  ownName?: string,
  locale: Locale = 'en',
): SupplierInfo {
  const key = supplierKey(data, s);
  let label: string;
  if (s.kind === 'own') {
    const name = ownName?.trim() ?? '';
    label = name === '' ? OWN_LABEL[locale] : `${name} (${OWN_SUFFIX[locale]})`;
  } else if (s.kind === 'house') label = HOUSE_LABEL[locale];
  else label = nameOf(data, s) || UNNAMED[locale];
  return { key, kind: s.kind, label };
}

/** Name shown for the own supplier: the board's setting wins over the signed-in user's name. */
export function ownSupplierName(data: BoardData, userName?: string): string | undefined {
  const own = data.meta.materialList?.ownName?.trim();
  return own !== undefined && own !== '' ? own : userName;
}

/**
 * Parses a typed supplier: "own" / "eigen…" / "", "house" / "haus", a musician's label on the
 * board, else another supplier with that name.
 */
export function parseSupplier(data: BoardData, text: string): Supplier {
  const t = text.trim();
  const lower = t.toLowerCase();
  if (t === '' || lower === 'own' || lower.startsWith('eigen')) return OWN;
  if (lower === 'house' || lower === 'haus' || lower === 'venue') return { kind: 'house' };
  const musician = Object.values(data.items).find(
    (i) => i.kind === 'musician' && i.label.trim().toLowerCase() === lower,
  );
  if (musician !== undefined)
    return { kind: 'musician', musicianId: musician.id, name: musician.label.trim() };
  return { kind: 'other', name: t };
}
